/**
 * The moderation queue, and the announcement board.
 *
 * Both are staff-only reads that nobody else in the app touches, so they live
 * beside the console rather than in the shared view file.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, serverUrl } from '@/lib/api';
import type { Announcement, AnnouncementField, AnnouncementKind, PublicUser } from '@/lib/types';
import { Icon } from '@/icons/Icon';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { Select } from '@/components/Select';
import { Embed } from '@/components/Embed';
import { openUserCard } from '@/components/user';
import { Button, Card, CardHead, Field, Input, Tag, Textarea, useConfirm, useToast } from '@/components/ui';

export type EvidenceLine = {
  id: string;
  body: string;
  userId: string;
  name: string;
  username: string | null;
  at: number;
  edited?: boolean;
  attachments?: { id: string; name: string; type: string; url: string }[];
  /** Set by the server on read: the copy is kept, this says if the original went. */
  goneSince?: boolean;
};

export type Evidence = {
  kind: 'message' | 'party' | 'recent';
  missing?: boolean;
  note?: string;
  messageId?: string;
  messages?: EvidenceLine[];
  takenAt?: number;
  /* party */
  title?: string;
  description?: string;
  gameId?: string | null;
  region?: string | null;
  closed?: boolean;
  members?: { userId: string; name: string; username: string | null; host: boolean }[];
} | null;

type Report = {
  id: string;
  reason: string;
  note: string;
  /** Taken when the report was raised - see server/evidence.js. */
  evidence?: Evidence;
  /** How often this person has been reported before, and how often upheld. */
  priors?: number;
  upheld?: number;
  /** What has already been done about them - see server/moderation.js. */
  warns?: number;
  timeouts?: number;
  silenced?: { until: number; reason: string; minutes: number } | null;
  targetBanned?: boolean;
  /** Where the account stands, worked out on the server. */
  standing?: { level: number; label: string } | null;
  resolvedByName?: string | null;
  status: 'open' | 'resolved' | 'dismissed';
  createdAt: number;
  by: PublicUser | null;
  target: PublicUser | null;
  /** Set when the report was raised against a party rather than a person. */
  partyId?: string | null;
  partyTitle?: string | null;
};

export function Reports() {
  const [showClosed, setShowClosed] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reports'],
    queryFn: () => api.get<{ reports: Report[] }>('/api/admin/reports')
  });

  async function resolve(id: string, status: 'resolved' | 'dismissed') {
    try {
      await api.post('/api/admin/reports/' + id + '/resolve', { status });
      toast(status === 'resolved' ? 'Marked resolved' : 'Dismissed', 'good');
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const all = data?.reports ?? [];
  const open = all.filter((r) => r.status === 'open');
  const list = showClosed ? all : open;

  if (isLoading) return <div className="h-[220px] animate-pulse rounded-[var(--radius-l)] bg-surface-2" />;

  return (
    <Card>
      <CardHead
        title="Reports"
        note={open.length + ' open'}
        actions={
          <Button kind="outline" size="sm" onClick={() => setShowClosed(!showClosed)}>
            {showClosed ? 'Open only' : 'Show closed'}
          </Button>
        }
      />

      {list.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-mute">
          {showClosed ? 'No reports at all.' : 'Nothing open.'}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {list.map((r) => (
            <div key={r.id} className="rounded-[var(--radius-s)] border border-line-soft bg-surface-1 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Tag kind={r.status === 'open' ? 'warn' : 'plain'}>{r.status}</Tag>
                <Tag>{r.reason}</Tag>
                {r.partyTitle ? <Tag kind="info">PARTY</Tag> : null}
                <span className="text-[12px] text-mute">{new Date(r.createdAt).toLocaleString()}</span>
                <span className="flex-1" />
                {r.status === 'open' ? (
                  <>
                    <Button size="sm" kind="primary" onClick={() => void resolve(r.id, 'resolved')}>Resolve</Button>
                    <Button size="sm" kind="ghost" onClick={() => void resolve(r.id, 'dismissed')}>Dismiss</Button>
                  </>
                ) : null}
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px]">
                <Party label={r.partyTitle ? 'Host' : 'About'} user={r.target} />
                <Party label="From" user={r.by} />
                {r.partyTitle ? (
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-bold tracking-[.06em] text-mute uppercase">Party</span>
                    <span className="font-semibold">{r.partyTitle}</span>
                  </span>
                ) : null}
              </div>

              {r.note ? (
                <p className="selectable mt-2 rounded-[var(--radius-s)] bg-surface-2 p-2.5 text-[13px] whitespace-pre-wrap text-dim">
                  {r.note}
                </p>
              ) : null}

              {/* What is already on their record. The single most useful
                  line on the page, and it was nowhere. */}
              <Priors report={r} />

              <EvidenceBlock ev={r.evidence ?? null} />

              {r.status === 'open' ? (
                <ReportActions report={r} onDone={() => { void queryClient.invalidateQueries({ queryKey: ['admin', 'reports'] }); }} />
              ) : null}

              {r.status !== 'open' && r.resolvedByName ? (
                <p className="m-0 mt-2 text-[11.5px] text-ghost">
                  {r.status === 'resolved' ? 'Upheld' : 'Dismissed'} by {r.resolvedByName}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Party({ label, user }: { label: string; user: PublicUser | null }) {
  return (
    <span className="flex items-center gap-2">
      <span className="text-[11px] font-bold tracking-[.06em] text-mute uppercase">{label}</span>
      {user ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); openUserCard(user, e.currentTarget); }}
          className="flex items-center gap-1.5 font-semibold hover:underline"
        >
          <Avatar user={user} size={22} />
          {user.displayName}
        </button>
      ) : (
        <span className="text-mute">deleted account</span>
      )}
    </span>
  );
}


/** One field changed, without mutating the array in place. */
function edit(list: AnnouncementField[], at: number, patch: Partial<AnnouncementField>): AnnouncementField[] {
  return list.map((f, i) => (i === at ? { ...f, ...patch } : f));
}

export function Announcements() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<AnnouncementKind>('info');

  /*
   * Everything below the body is optional.
   *
   * An announcement should be able to be one line when that is all there is
   * to say, and a full page when it is a patch note. The preview beside the
   * form shows exactly what people will get, so nobody has to post one to
   * find out how it looks.
   */
  const [subtitle, setSubtitle] = useState('');
  const [footer, setFooter] = useState('');
  const [link, setLink] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [fields, setFields] = useState<AnnouncementField[]>([]);
  const [image, setImage] = useState<string | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const { data } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ announcements: Announcement[] }>('/api/announcements')
  });

  /** What the preview renders, and what gets posted. Same object. */
  const draft: Announcement = {
    id: 'preview',
    title: title.trim() || 'Untitled announcement',
    body,
    kind,
    createdAt: Date.now(),
    subtitle: subtitle.trim() || null,
    fields: fields.filter((f) => f.name.trim() && f.value.trim()),
    image,
    thumbnail,
    footer: footer.trim() || null,
    link: link.trim() || null,
    linkLabel: linkLabel.trim() || null
  };

  async function post() {
    setBusy(true);
    try {
      const { id, createdAt, ...payload } = draft;
      void id; void createdAt;
      await api.post('/api/admin/announcements', payload);

      setTitle(''); setBody(''); setSubtitle(''); setFooter('');
      setLink(''); setLinkLabel(''); setFields([]); setImage(null); setThumbnail(null);

      toast('Announcement posted', 'good');
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  /** Pictures go through the normal upload path, so the same limits apply. */
  async function pick(into: (url: string) => void) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const res = await fetch(serverUrl() + '/api/files', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
            'Content-Type': file.type || 'application/octet-stream',
            'X-File-Name': encodeURIComponent(file.name),
            'X-File-Kind': 'attachment'
          },
          body: file
        });
        const data = await res.json() as { file?: { url: string }; error?: string };
        if (!data.file) throw new Error(data.error ?? 'Upload failed');
        into(data.file.url);
      } catch (err) {
        toast((err as Error).message, 'bad');
      }
    };
    input.click();
  }

  async function remove(id: string) {
    const ok = await confirm('Delete announcement?', 'It disappears for everyone.', {
      confirmLabel: 'Delete',
      danger: true
    });
    if (!ok) return;
    try {
      await api.del('/api/admin/announcements/' + id);
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  return (
    <>
      <Card>
        <CardHead title="Post an announcement" note="Everyone sees it the next time they open the app" />
        <div className="flex flex-col gap-[var(--gap)]">
          <div className="grid gap-[var(--gap)] sm:grid-cols-[1fr_170px]">
            <Field label="Title">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                placeholder="Maintenance tonight"
              />
            </Field>
            <Field label="Kind">
              <Select
                search={false}
                value={kind}
                onChange={(v) => setKind(v as AnnouncementKind)}
                options={[
                  { value: 'info', label: 'Announcement', icon: 'megaphone', color: 'var(--accent)' },
                  { value: 'update', label: 'Update', icon: 'sparkle', color: 'var(--accent-hi)' },
                  { value: 'success', label: 'Now live', icon: 'checkCirc', color: 'var(--good)' },
                  { value: 'event', label: 'Event', icon: 'trophy', color: 'var(--mythic)' },
                  { value: 'warning', label: 'Heads up', icon: 'shieldNav', color: 'var(--warn)' },
                  { value: 'danger', label: 'Important', icon: 'ban', color: 'var(--bad)' }
                ]}
              />
            </Field>
          </div>
          <Field label="Subtitle" hint="One line under the title. Optional.">
            <Input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              maxLength={140}
              placeholder="Starting at 22:00 UTC"
            />
          </Field>

          <Field label="Body" hint="**bold**, `code`, and lines starting with - become bullets.">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={3000}
              rows={6}
              placeholder={'What is happening, and when.\n\n- One thing\n- Another thing'}
            />
          </Field>

          {/* Detail rows. Two sit side by side unless one takes the width. */}
          <Field label="Details" hint="Short labelled facts — version, duration, who it affects.">
            <div className="flex flex-col gap-2">
              {fields.map((f, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                  <Input
                    value={f.name}
                    onChange={(e) => setFields(edit(fields, i, { name: e.target.value }))}
                    placeholder="Label"
                    className="w-[150px]"
                  />
                  <Input
                    value={f.value}
                    onChange={(e) => setFields(edit(fields, i, { value: e.target.value }))}
                    placeholder="Value"
                    className="min-w-0 flex-1"
                  />
                  <Button
                    size="sm"
                    kind={f.inline ? 'primary' : 'outline'}
                    onClick={() => setFields(edit(fields, i, { inline: !f.inline }))}
                  >
                    {f.inline ? 'Half' : 'Full'}
                  </Button>
                  <Button size="sm" kind="ghost" onClick={() => setFields(fields.filter((_, x) => x !== i))}>
                    Remove
                  </Button>
                </div>
              ))}
              {fields.length < 8 ? (
                <Button
                  size="sm"
                  kind="outline"
                  onClick={() => setFields([...fields, { name: '', value: '', inline: true }])}
                >
                  Add a detail
                </Button>
              ) : null}
            </div>
          </Field>

          <div className="grid gap-[var(--gap)] sm:grid-cols-2">
            <Field label="Banner image" hint="Sits under the text, full width.">
              <div className="flex items-center gap-2">
                <Button size="sm" kind="outline" onClick={() => void pick(setImage)}>
                  {image ? 'Replace' : 'Choose'}
                </Button>
                {image ? (
                  <Button size="sm" kind="ghost" onClick={() => setImage(null)}>Remove</Button>
                ) : <span className="text-[12px] text-mute">None</span>}
              </div>
            </Field>
            <Field label="Thumbnail" hint="Small, beside the title. A game icon or badge.">
              <div className="flex items-center gap-2">
                <Button size="sm" kind="outline" onClick={() => void pick(setThumbnail)}>
                  {thumbnail ? 'Replace' : 'Choose'}
                </Button>
                {thumbnail ? (
                  <Button size="sm" kind="ghost" onClick={() => setThumbnail(null)}>Remove</Button>
                ) : <span className="text-[12px] text-mute">None</span>}
              </div>
            </Field>
          </div>

          <div className="grid gap-[var(--gap)] sm:grid-cols-[1fr_1fr_160px]">
            <Field label="Link" hint="Opens in the browser.">
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://…"
                spellCheck={false}
              />
            </Field>
            <Field label="Button label">
              <Input
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                maxLength={40}
                placeholder="Read the notes"
              />
            </Field>
            <Field label="Footer">
              <Input
                value={footer}
                onChange={(e) => setFooter(e.target.value)}
                maxLength={140}
                placeholder="Pugo team"
              />
            </Field>
          </div>

          {/* Exactly what everyone will see, updating as you type. */}
          <Field label="Preview">
            <Embed item={draft} />
          </Field>

          <div className="flex">
            <div className="flex-1" />
            <Button kind="primary" disabled={busy || !title.trim()} onClick={() => void post()}>
              {busy ? 'Posting…' : 'Post announcement'}
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title="Posted" note={(data?.announcements.length ?? 0) + ' live'} />
        {data?.announcements.length ? (
          <div className="flex flex-col gap-2">
            {data.announcements.map((a) => (
              <div key={a.id} className="flex items-start gap-3">
                <div className="min-w-0 flex-1"><Embed item={a} compact /></div>
                <Button size="sm" kind="ghost" onClick={() => void remove(a.id)}>Delete</Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-[13px] text-mute">Nothing posted.</p>
        )}
      </Card>
      {dialog}
    </>
  );
}

/* ------------------------------------------------------- the evidence */

/**
 * What was actually said, as it was when the report was raised.
 *
 * Kept with the report rather than fetched now, so deleting the message
 * afterwards does not take it with it - which is the first thing anybody
 * does after being reported. A line that has gone since is marked, because
 * "they said it and deleted it" and "they said it and left it up" are
 * different conversations.
 *
 * The reported line is picked out of its neighbours rather than shown
 * alone: one message out of context proves nothing either way.
 */
function EvidenceBlock({ ev }: { ev: Evidence }) {
  const [open, setOpen] = useState(false);

  if (!ev) return null;

  if (ev.kind === 'party') {
    return (
      <div className="mt-2 rounded-[var(--radius-s)] bg-surface-2 p-3">
        <p className="m-0 mb-2 text-[11px] font-bold tracking-[.06em] text-mute uppercase">
          The party, as it read when it was reported
        </p>
        <p className="m-0 text-[14px] font-bold text-ink">{ev.title || '(no title)'}</p>
        {ev.description ? (
          <p className="selectable m-0 mt-1 text-[13px] whitespace-pre-wrap text-dim">{ev.description}</p>
        ) : null}
        <p className="m-0 mt-2 text-[12px] text-mute">
          {ev.gameId ? ev.gameId + ' · ' : ''}{ev.region ? ev.region + ' · ' : ''}
          {(ev.members ?? []).length} {(ev.members ?? []).length === 1 ? 'member' : 'members'}
          {ev.closed ? ' · closed since' : ''}
        </p>
        {(ev.members ?? []).length ? (
          <p className="m-0 mt-1 text-[12px] text-ghost">
            {(ev.members ?? []).map((m) => m.name + (m.host ? ' (host)' : '')).join(', ')}
          </p>
        ) : null}
      </div>
    );
  }

  if (ev.missing) {
    return (
      <p className="mt-2 flex items-center gap-2 rounded-[var(--radius-s)] bg-surface-2 p-2.5 text-[12.5px] text-mute">
        <Icon name="alert" size={14} className="flex-none text-warn" />
        {ev.note || 'Nothing was captured for this one.'}
      </p>
    );
  }

  const lines = ev.messages ?? [];
  const shown = open ? lines : lines.slice(Math.max(0, lines.findIndex((m) => m.id === ev.messageId) - 2));

  return (
    <div className="mt-2 rounded-[var(--radius-s)] bg-surface-2 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="m-0 flex-1 text-[11px] font-bold tracking-[.06em] text-mute uppercase">
          {ev.kind === 'recent' ? 'What they said last' : 'The conversation, as it was'}
        </p>
        {lines.length > shown.length || open ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-[6px] px-1.5 text-[12px] font-semibold text-accent outline-none hover:underline"
          >
            {open ? 'Show less' : 'Show all ' + lines.length}
          </button>
        ) : null}
      </div>

      <ol className="m-0 flex list-none flex-col gap-1 p-0">
        {shown.map((m) => {
          const isTarget = m.id === ev.messageId;
          return (
            <li
              key={m.id}
              className={cn(
                'rounded-[calc(8px*var(--radius-mul,1))] px-2 py-1.5 text-[13px]',
                isTarget
                  ? 'bg-[color-mix(in_srgb,var(--bad)_13%,transparent)] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_35%,transparent)]'
                  : 'bg-surface-1'
              )}
            >
              <p className="m-0 flex flex-wrap items-baseline gap-x-2">
                <span className={cn('font-bold', isTarget ? 'text-bad' : 'text-ink')}>{m.name}</span>
                <span className="text-[11px] text-ghost">{new Date(m.at).toLocaleTimeString()}</span>
                {m.edited ? <span className="text-[11px] text-ghost">(edited)</span> : null}
                {isTarget ? <span className="text-[11px] font-bold text-bad">reported</span> : null}
                {m.goneSince ? (
                  <span className="rounded-full bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] px-1.5 text-[10.5px] font-bold text-warn">
                    deleted since
                  </span>
                ) : null}
              </p>
              <p className="selectable m-0 mt-0.5 whitespace-pre-wrap text-dim">{m.body || <span className="text-ghost">(no text)</span>}</p>
              {(m.attachments ?? []).length ? (
                <p className="m-0 mt-1 text-[11.5px] text-ghost">
                  {(m.attachments ?? []).length} attachment{(m.attachments ?? []).length === 1 ? '' : 's'}: {(m.attachments ?? []).map((a) => a.name).join(', ')}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      <p className="m-0 mt-2 text-[11px] text-ghost">
        Copied when the report was raised{ev.takenAt ? ' · ' + new Date(ev.takenAt).toLocaleString() : ''}. Deleting a message does not remove it from here.
      </p>
    </div>
  );
}

/* -------------------------------------------------------- the actions */

/**
 * What to do about it, on the report.
 *
 * Banning used to be the only thing a moderator could reach, so every report
 * was a choice between nothing and ending somebody's account - and faced
 * with that, people pick nothing. A word settles most first offences and an
 * hour off settles most arguments; neither existed.
 *
 * Deliberately in order of severity, left to right, with the heavy one
 * separated and confirmed. Resolving is what closes the report; the actions
 * are separate, because "warned and resolved" and "resolved without doing
 * anything" are different outcomes and the record should say which.
 */
function ReportActions({ report, onDone }: { report: Report; onDone: () => void }) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState('');
  const [why, setWhy] = useState('');

  const target = report.target;
  if (!target) return null;

  async function run(label: string, fn: () => Promise<unknown>) {
    if (busy) return;
    setBusy(label);
    try {
      await fn();
      toast(label + ' · ' + (target!.displayName || target!.username), 'good');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy('');
    }
  }

  const reason = why.trim() || report.note || report.reason;

  return (
    <div className="mt-2.5 flex flex-col gap-2 rounded-[var(--radius-s)] bg-surface-2 p-2.5">
      {dialog}

      <input
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        placeholder="What they are being told (defaults to the report)"
        maxLength={300}
        className="w-full rounded-[calc(9px*var(--radius-mul,1))] bg-surface-1 px-2.5 py-1.5 text-[12.5px] outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_1px_var(--accent)]"
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          size="sm"
          kind="outline"
          disabled={!!busy}
          onClick={() => void run('Warned', () =>
            api.post('/api/admin/users/' + target.id + '/warn', { reason, reportId: report.id }))}
        >
          {busy === 'Warned' ? 'Warning…' : 'Warn'}
        </Button>

        {[
          { label: '1h', minutes: 60 },
          { label: '24h', minutes: 60 * 24 },
          { label: '7d', minutes: 60 * 24 * 7 }
        ].map((t) => (
          <Button
            key={t.label}
            size="sm"
            kind="outline"
            disabled={!!busy}
            onClick={() => void run('Timed out ' + t.label, () =>
              api.post('/api/admin/users/' + target.id + '/timeout', { minutes: t.minutes, reason, reportId: report.id }))}
          >
            Mute {t.label}
          </Button>
        ))}

        {/* Only when there is a message to take down, and only once. */}
        {report.evidence?.kind === 'message' && report.evidence.messageId
          && !report.evidence.messages?.find((m) => m.id === report.evidence!.messageId)?.goneSince ? (
          <Button
            size="sm"
            kind="outline"
            disabled={!!busy}
            onClick={() => void run('Message removed', () =>
              api.del('/api/admin/messages/' + report.evidence!.messageId))}
          >
            Delete the message
          </Button>
        ) : null}

        <span className="flex-1" />

        <Button
          size="sm"
          kind="danger"
          disabled={!!busy}
          onClick={async () => {
            const sure = await confirm(
              'Ban ' + (target.displayName || target.username) + '?',
              'They are signed out everywhere and cannot sign back in. Use a mute if this is an argument rather than a pattern.',
              { confirmLabel: 'Ban them', danger: true }
            );
            if (!sure) return;
            void run('Banned', () => api.post('/api/admin/users/' + target.id + '/ban', { reason, days: 0 }));
          }}
        >
          Ban
        </Button>
      </div>
    </div>
  );
}

/** Warned twice, timed out once - the line that decides a third report. */
function Priors({ report }: { report: Report }) {
  const bits: string[] = [];
  if (report.priors) bits.push(report.priors + ' report' + (report.priors === 1 ? '' : 's') + ' before');
  if (report.upheld) bits.push(report.upheld + ' upheld');
  if (report.warns) bits.push(report.warns + ' warning' + (report.warns === 1 ? '' : 's'));
  if (report.timeouts) bits.push(report.timeouts + ' mute' + (report.timeouts === 1 ? '' : 's'));
  if (report.silenced) bits.push('muted for another ' + report.silenced.minutes + 'm');
  if (report.targetBanned) bits.push('already banned');

  /*
   * Where they stand, said once, in front of the counting.
   *
   * "Two warnings and a mute" is arithmetic the moderator has to do before
   * they can decide anything; "Very limited" is the answer. The counting
   * stays after it, because the answer without its reason is just a label.
   */
  const st = report.standing;
  const tone = !st || st.level === 0 ? 'var(--good)' : st.level >= 3 ? 'var(--bad)' : 'var(--warn)';
  const badge = st ? (
    <span
      className="rounded-full px-2 py-[1px] text-[11px] font-bold"
      style={{ background: 'color-mix(in srgb, ' + tone + ' 16%, transparent)', color: tone }}
    >
      {st.label}
    </span>
  ) : null;

  if (!bits.length) {
    return (
      <p className="m-0 mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-ghost">
        {badge}
        Nothing on record before this.
      </p>
    );
  }

  return (
    <p className="m-0 mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-mute">
      {badge}
      <Icon name="clock" size={13} className="flex-none text-warn" />
      {bits.join(' · ')}
    </p>
  );
}
